<template>
  <v-card tile>
    <v-card-text>
      <v-snackbar v-model="snackbar" :top="true" :timeout="3000" color="white" light>
        {{ snackbarText }}
        <v-btn color="green" text @click="snackbar = false">Fermer</v-btn>
      </v-snackbar>
      <v-dialog v-model="bulkWarningDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">
            <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
            Attention
          </v-card-title>
          <v-card-text>
            Vous avez sélectionné {{ selectedSessions.length }} sessions. Êtes-vous sûr de vouloir envoyer autant d'emails?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="bulkWarningDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-row align="center">
        <v-col cols="12" md="4">
          <div v-if="selectedSessions.length">
            <v-btn
              color="primary"
              :disabled="!canSendEmails"
              :depressed="!canSendEmails"
              @click.native="sendEmails()"
              :loading="loading"
              class="mb-3"
            >
              Envoyer des emails
              <v-icon right dark>mdi-email-fast</v-icon>
            </v-btn>
            <div class="d-flex">
              <div class="email-section mr-3">
                <div class="email-section-title">Fichier</div>
                <v-checkbox v-model="presence" label="Relevé de fréquentation" hide-details dense></v-checkbox>
                <v-checkbox v-model="balance" label="Bilan" hide-details dense></v-checkbox>
                <v-checkbox v-model="attestation" label="Fiche apprenant" hide-details dense></v-checkbox>
              </div>
              <div class="email-section">
                <div class="email-section-title">Destinataire</div>
                <v-checkbox v-model="sendToAssistant" label="Assistant social" hide-details dense></v-checkbox>
                <v-checkbox v-model="sendToStudent" label="Élève" hide-details dense></v-checkbox>
                <v-checkbox v-model="sendToCustomEmail" label="Autre" hide-details dense></v-checkbox>
                <v-expand-transition>
                  <v-text-field
                    v-if="sendToCustomEmail"
                    v-model.trim="$v.customEmail.$model"
                    label="Email"
                    dense
                    outlined
                    hide-details="auto"
                    class="mt-2"
                    :error-messages="$v.customEmail.$error ? 'Email invalide' : ''"
                    @input="delayTouch($v.customEmail)"
                    @blur="$v.customEmail.$touch()"
                  ></v-text-field>
                </v-expand-transition>
              </div>
            </div>
          </div>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-dialog ref="dialog" v-model="dialog" persistent width="900">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="date-selector">
                <template v-if="dates && dates[0] && dates[1]">
                  <v-icon small color="green darken-1" class="mr-1">mdi-calendar-start</v-icon>
                  <span class="text-no-wrap">{{ dates[0] | dateToHuman }}</span>
                  <v-icon small class="mx-2">mdi-arrow-right</v-icon>
                  <v-icon small color="red darken-1" class="mr-1">mdi-calendar-end</v-icon>
                  <span class="text-no-wrap">{{ dates[1] | dateToHuman }}</span>
                </template>
                <span v-else class="grey--text text-caption">Sélectionner une période</span>
              </div>
            </template>
            <v-card>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-list dense>
                      <v-subheader>Preset</v-subheader>
                      <v-list-item-group color="primary">
                        <v-list-item
                          v-for="preset in date_presets"
                          :key="preset.label"
                          @click="onDateRangeChange(preset.range)"
                        >
                          <v-list-item-content>
                            <v-list-item-title v-text="preset.label"></v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-col>
                  <v-col cols="12" md="9">
                    <v-date-picker
                      v-model="dates"
                      range
                      locale="fr-ch"
                      :first-day-of-week="1"
                      @input="onDateRangeChange"
                    ></v-date-picker>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dialog = false">Annuler</v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="
                    dialog = false
                    getDataFromApi()
                  "
                >
                  OK
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            prepend-inner-icon="mdi-magnify"
            label="Chercher"
            clearable
            outlined
            dense
            hide-details
            v-model="search"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col cols="12" md="3">
          <v-autocomplete
            placeholder="Nom du cours"
            :items="availableCourseNames"
            v-model="filters.course_name"
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-book-outline"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            multiple
            placeholder="Institution référente"
            :items="availableAssistantOrgs"
            v-model="filters.assistance_society"
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-domain"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            placeholder="Assistant social"
            :items="availableAssistantNames"
            v-model="filters.assistance_name"
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-account-tie"
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- data table -->
    <v-data-table
      v-model="selectedSessions"
      show-select
      :search="search"
      :custom-filter="customFilter"
      :headers="headers"
      :items="student_courses"
      :loading="loading"
      :items-per-page="200"
      item-key="id"
    >
      <template v-slot:item.course.start="{ item }">
        <div class="d-flex align-center">
          <v-icon small color="green darken-1" class="mr-1">mdi-calendar-start</v-icon>
          <span class="text-no-wrap">{{ item.course.start | dateToHuman }}</span>
        </div>
      </template>
      <template v-slot:item.course.end="{ item }">
        <div class="d-flex align-center">
          <v-icon small color="red darken-1" class="mr-1">mdi-calendar-end</v-icon>
          <span class="text-no-wrap">{{ item.course.end | dateToHuman }}</span>
        </div>
      </template>
      <template v-slot:item.course.type="{ item }">
        <v-tooltip bottom v-if="item.course.type">
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              small
              :color="getCourseTypeColor(item.course.type)"
              text-color="white"
              label
              v-bind="attrs"
              v-on="on"
            >
              <template v-if="item.course.type === 'frdistance'">
                FR
                <v-icon small right>mdi-webcam</v-icon>
              </template>
              <template v-else>
                {{ item.course.type ? item.course.type.toUpperCase() : "" }}
              </template>
            </v-chip>
          </template>
          <span>{{ item.course.type_full || item.course.type }}</span>
        </v-tooltip>
        <span v-else class="grey--text">-</span>
      </template>
      <template v-slot:item.course.name="{ item }">
        <span class="font-weight-medium">{{ item.course.name }}</span>
      </template>
      <template v-slot:item.student.last_name="{ item }">
        <span class="font-weight-medium">{{ item.student.last_name }}</span>
      </template>
      <template v-slot:item.assistance.society="{ item }">
        <v-chip
          v-if="item.assistance.society"
          small
          :color="getHashColor(item.assistance.society)"
          text-color="white"
        >
          {{ item.assistance.society }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>
      <template v-slot:item.assistance.name="{ item }">
        <v-chip v-if="item.assistance.name" small :color="getHashColor(item.assistance.name)" text-color="white">
          {{ item.assistance.name }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>
      <template v-slot:item.notification_status="{ item }">
        <v-tooltip bottom v-if="item.notification_status == true">
          <template v-slot:activator="{ on, attrs }">
            <v-icon color="green" v-bind="attrs" v-on="on">mdi-email-check</v-icon>
          </template>
          <span>Email envoyé</span>
        </v-tooltip>
        <v-tooltip bottom v-else-if="item.notification_status == false && item.notification_status !== null">
          <template v-slot:activator="{ on, attrs }">
            <v-icon color="red" v-bind="attrs" v-on="on">mdi-email-remove</v-icon>
          </template>
          <span>Échec d'envoi</span>
        </v-tooltip>
        <span v-else class="grey--text">-</span>
      </template>
      <template v-slot:item.last_sent_notification="{ item }">
        <div v-if="item.last_sent_notification" class="d-flex align-center">
          <v-icon small color="blue-grey" class="mr-1">mdi-clock-outline</v-icon>
          <span class="text-caption">{{ item.last_sent_notification }}</span>
        </div>
        <span v-else class="grey--text">-</span>
      </template>
      <template v-slot:item.details="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :to="`/students/${item.student_id}`" text icon color="purple" v-bind="attrs" v-on="on">
              <v-icon>mdi-information-outline</v-icon>
            </v-btn>
          </template>
          <span>Voir les détails de l'élève</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import StudentCourse from "@/models/StudentCourse"
import { date_presets, getHashColor, getCourseTypeColor } from "@/utils/helpers.js"
import { email } from "vuelidate/lib/validators"

export default {
  data() {
    return {
      loading: true,
      date_presets,
      student_courses: [],
      balance: false,
      presence: false,
      attestation: false,
      sendToAssistant: true,
      sendToStudent: false,
      sendToCustomEmail: false,
      customEmail: "",
      touchTimeout: null,
      snackbar: false,
      snackbarText: "Action effectuée",
      pagination: {
        sortBy: "name",
      },
      selectedSessions: [],
      search: "",
      filters: {
        course_name: "",
        assistance_society: [],
        assistance_name: "",
      },
      dates: [
        new Date().toLocaleDateString("en-CA"), //start date today. YYYY-MM-DD
        new Date().toLocaleDateString("en-CA"), //start date today. YYYY-MM-DD
      ],
      menu: false,
      valid: false,
      dialog: false,
      formTitle: "Ouvrir un nouveau cours",
      edit: false,
      bulkWarningDialog: false,
      bulkWarningShown: false,
    }
  },
  validations: {
    customEmail: { email },
  },
  async mounted() {
    this.loading = true
    await this.getDataFromApi()
    this.loading = false
  },
  watch: {
    "selectedSessions.length"(newLen) {
      if (newLen > 10 && !this.bulkWarningShown) {
        this.bulkWarningDialog = true
        this.bulkWarningShown = true
      } else if (newLen <= 10) {
        this.bulkWarningShown = false
      }
    },
  },
  computed: {
    headers() {
      return [
        { text: "Début", value: "course.start", width: "120px" },
        { text: "Fin", value: "course.end", width: "120px" },
        { text: "Type", value: "course.type", width: "80px" },
        {
          text: "Nom du cours",
          value: "course.name",
          filter: (value) => {
            if (!this.filters.course_name) return true
            return value === this.filters.course_name
          },
        },
        { text: "Prénom", value: "student.first_name" },
        { text: "Nom", value: "student.last_name" },
        {
          text: "Assistance",
          value: "assistance.society",
          filter: (value) => {
            if (!this.filters.assistance_society?.length) return true
            return this.filters.assistance_society.includes(value)
          },
        },
        {
          text: "Assistant(e)",
          value: "assistance.name",
          filter: (value) => {
            if (!this.filters.assistance_name) return true
            return value === this.filters.assistance_name
          },
        },
        { text: "Email", value: "notification_status", width: "120px" },
        { text: "Date envoi", value: "last_sent_notification", width: "140px" },
        { text: "Détails", value: "details", sortable: false, width: "80px" },
      ]
    },
    canSendEmails() {
      const isCustomEmailValid = this.customEmail && !this.$v.customEmail.$invalid
      const hasValidCustomEmail = !this.sendToCustomEmail || isCustomEmailValid
      const hasRecipient = this.sendToAssistant || this.sendToStudent || (this.sendToCustomEmail && isCustomEmailValid)
      return (
        this.selectedSessions.length > 0 &&
        (this.balance || this.presence || this.attestation) &&
        hasRecipient &&
        hasValidCustomEmail
      )
    },
    availableAssistantOrgs() {
      let filtered = this.student_courses || []
      if (this.filters.course_name) {
        filtered = filtered.filter((sc) => sc.course.name === this.filters.course_name)
      }
      if (this.filters.assistance_name) {
        filtered = filtered.filter((sc) => sc.assistance.name === this.filters.assistance_name)
      }
      return [...new Set(filtered.map((sc) => sc.assistance.society).filter(Boolean))]
    },
    availableAssistantNames() {
      let filtered = this.student_courses || []
      if (this.filters.course_name) {
        filtered = filtered.filter((sc) => sc.course.name === this.filters.course_name)
      }
      if (this.filters.assistance_society?.length) {
        filtered = filtered.filter((sc) => this.filters.assistance_society.includes(sc.assistance.society))
      }
      return [...new Set(filtered.map((sc) => sc.assistance.name).filter(Boolean))]
    },
    availableCourseNames() {
      let filtered = this.student_courses || []
      if (this.filters.assistance_society?.length) {
        filtered = filtered.filter((sc) => this.filters.assistance_society.includes(sc.assistance.society))
      }
      if (this.filters.assistance_name) {
        filtered = filtered.filter((sc) => sc.assistance.name === this.filters.assistance_name)
      }
      return [...new Set(filtered.map((sc) => sc.course.name).filter(Boolean))]
    },
  },
  methods: {
    delayTouch($v) {
      $v.$reset()
      if (this.touchTimeout) clearTimeout(this.touchTimeout)
      this.touchTimeout = setTimeout(() => {
        $v.$touch()
      }, 500)
    },
    onDateRangeChange(t) {
      t.sort((a, b) => Date.parse(a) - Date.parse(b))
      this.dates = t
    },
    async getDataFromApi() {
      this.loading = true
      await StudentCourse.deleteAll()
      await StudentCourse.getEnhancedStudentCoursesBetweenDates(this.dates[0], this.dates[1])
      this.student_courses = StudentCourse.query().with("course").with("student").all()
      this.selectedSessions = []
      this.selected = []

      this.loading = false
    },
    startLoading() {
      this.loading = true
      return true
    },
    sendEmails() {
      let selected_student_course_ids = this.selectedSessions.map((row) => row.id)
      let list_of_student_names = this.selectedSessions.map((row) => row.student.last_name)

      // Build recipient list based on checkboxes
      let recipientTypes = []
      if (this.sendToAssistant) recipientTypes.push("assistant social")
      if (this.sendToStudent) recipientTypes.push("élève")
      if (this.sendToCustomEmail && this.customEmail) recipientTypes.push(this.customEmail)

      if (selected_student_course_ids.length <= 0) return
      confirm(
        `Attention: vous êtes sur le point d'envoyer des emails à ${recipientTypes.join(" et ")} concernant ${list_of_student_names.join(", ")}!`
      ) &&
        this.startLoading() &&
        axios
          .post("/api/v1/sendemails", {
            student_course_id_list: selected_student_course_ids,
            balance: this.balance,
            presence: this.presence,
            attestation: this.attestation,
            sendToAssistant: this.sendToAssistant,
            sendToStudent: this.sendToStudent,
            customEmail: this.sendToCustomEmail ? this.customEmail : "",
          })
          .then((r) => {
            this.snackbarText = "Emails envoyés avec succès"
            this.snackbar = true
          })
          .catch((error) => {
            this.snackbarText = error + " "
            this.snackbarText += error.response?.data || ""
            this.snackbar = true
          })
          .finally(() => {
            this.loading = false
            this.getDataFromApi()
          })
    },
    customFilter(value, search, item) {
      if (!search) return true
      const s = search.toLowerCase()
      return [
        item.course?.name,
        item.course?.type,
        item.student?.first_name,
        item.student?.last_name,
        item.assistance?.society,
        item.assistance?.name,
      ].some((v) => v && String(v).toLowerCase().includes(s))
    },
    getHashColor,
    getCourseTypeColor,
  },
}
</script>

<style scoped>
.date-selector {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  width: fit-content;
}

.date-selector:hover {
  border-color: rgba(0, 0, 0, 0.87);
}

.email-section {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 12px;
  min-height: 160px;
  flex: 1 1 0;
}

.email-section-title {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}
</style>
